import { Model } from '@shared/models/Model';
import type { ChipConfig, DataTableHeaders, DotNotationOf } from '@shared/types/Vuetify';
import type { IModel } from '@shared/types/Model';
import { routes, type IndexPayoutIncludes, type AllPayoutIncludes } from '@app/purchase/payout/routes/routes';
import type { ValuesOfConst } from '@shared/types/ApiResponse.ts';
import { useOwnerRepository } from '@app/land-hold/owner/repositories/useOwnerRepository.ts';
import { useBlockRepository } from '@app/purchase/block/repositories/useBlockRepository.ts';
import { useTransactionRepository } from '@app/purchase/transaction/repositories/useTransactionRepository.ts';
import { PayoutEvent } from '@app/purchase/bundle/models/PayoutEvent.ts';
import type { CsvStructure } from '@shared/composables/useDownloadCsv.ts';

export const PayoutStatus = {
    Pending: 'pending',
    Unresolved: 'unresolved',
    Paid: 'paid',
} as const;

export interface IPayout extends IModel {
    status: ValuesOfConst<typeof PayoutStatus>;
    amount: number;
    price: number;
    currency: string;
    note: string;
}

export class Payout extends Model implements IPayout {
    status!: ValuesOfConst<typeof PayoutStatus>;
    amount!: number;
    price!: number;
    currency!: string;
    note!: string;

    public constructor(o: IPayout) {
        super(o);
        Object.assign(this, o);
    }

    public static override chipConfig(field: DotNotationOf<AllPayoutIncludes>, item: AllPayoutIncludes): ChipConfig {
        let color;
        switch (field) {
            case 'status':
                color = Payout.getStatusColor(item.status);
                return { size: 'small', color, text: item.status };
            case 'bundle.data.status':
                if (item.bundle) {
                    color = PayoutEvent.getStatusColor(item.bundle.data.status);
                    return { size: 'small', color, text: item.bundle.data.status };
                }
                return { size: 'small' };
            default:
                return { size: 'small' };
        }
    }

    public static override chips(): DotNotationOf<AllPayoutIncludes>[] {
        return ['status', 'bundle.data.status'];
    }

    public static getStatusColor(status: ValuesOfConst<typeof PayoutStatus>): string {
        switch (status) {
            case PayoutStatus.Unresolved:
                return 'warning';
            case PayoutStatus.Pending:
                return 'grey';
            case PayoutStatus.Paid:
                return 'success';
            default:
                return 'grey';
        }
    }

    public static statuses(): string[] {
        return Object.values(PayoutStatus);
    }

    public static override headers(): DataTableHeaders<IndexPayoutIncludes> {
        return [
            { title: 'Actions', key: 'actions', sortable: false, fixed: true, width: 113, minWidth: '113', headerProps: { style: { borderRight: '1px solid rgba(0, 0, 0, 0.12)' } } },
            { title: 'Owner', key: 'owner.data.name', sortable: false, link: (item) => (item.owner ? useOwnerRepository().query.buildGetUrl(item.owner.data.id).endpoint : undefined) },
            { title: 'Block Serial Code', key: 'block.data.block_serial_code', sortable: false, link: (item) => (item.block ? useBlockRepository().query.show(item.block.data.id).endpoint : undefined) },
            { title: 'Transaction Code', key: 'transaction.data.transaction_code', sortable: false, link: (item) => (item.transaction ? useTransactionRepository().query.show(item.transaction.data.id).endpoint : undefined) },
            { title: 'Status', key: 'status' },
            { title: 'Total', key: 'amount' },
            { title: 'Currency', key: 'currency', sortable: false },
            { title: 'Payout', key: 'price' },
            { title: 'Note', key: 'note', sortable: false },
            { title: 'Payout Event', key: 'bundle.data.name', sortable: false },
            { title: 'PE Status', key: 'bundle.data.status', sortable: false },
            { title: 'PE Date', key: 'bundle.data.date', date: true, sortable: false },
            { title: 'Created At', key: 'created_at' },
            { title: 'Updated At', key: 'updated_at' },
        ];
    }

    public static csvHeaders(): CsvStructure[] {
        return Payout.headers()
            .filter((header) => header.key !== 'actions')
            .map((header) => ({ title: header.title, key: header.key })) as CsvStructure[];
    }

    public static override routes(): ReturnType<typeof routes> {
        return routes();
    }
}
